/* eslint-disable camelcase */

// node modules
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useStyles from 'isomorphic-style-loader/useStyles';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

// app modules: components
import AlacarteStatusIndicator from 'components/AlacarteStatusIndicator';
import { ContentOptionsAsIcon } from 'components/ContentOptions';
import FavouriteButton from 'components/FavouriteButton';
import Link from 'components/Link';
import StarRating from 'components/StarRating';

// app modules: services
import assets from 'services/assets';
import { getImageSrc, getImageSrcSet } from 'services/image';
import route from 'services/route';
import formatter from 'services/formatter';
import { getSharingText } from 'services/socialShare';

// app modules: props
import AuthorsProps from 'components/Authors/AuthorsProps';
import RecipeImageProps from 'components/RecipeImage/RecipeImageProps';

// app modules: constants
import { CONTENT_TYPES } from 'constants/index';

// assets
import CssContentTile from './ContentTile.scss';
import useMediaQuery from '@material-ui/core/useMediaQuery';

function ContentTileImage(props) {
  const { bookCoverImageName, primary_image, mobileView } = props;
  const title = '';

  if (primary_image) {
    return (
      <img
        loading="lazy"
        className="content-tile__image"
        src={getImageSrc(primary_image, 'square', size => size.w >= 315)}
        srcSet={getImageSrcSet(primary_image, 'square', size => size.w >= 100 && size.w <= 640)}
        sizes={mobileView === 'list' ? '(max-width: 480px) 100px, 100vw' : '100vw'}
        alt={title}
      />
    );
  }

  return (
    <img
      className="content-tile__image content-tile__image--book-cover"
      loading="lazy"
      src={assets.coverUrl(bookCoverImageName, {
        resize: 'fit-in',
        width: 240,
        height: 240,
      })}
      srcSet={assets.srcSet(
        bookCoverImageName,
        [{ w: 240, h: 240, resize: 'fit-in' }, { w: 480, h: 480, resize: 'fit-in' }],
        assets.coverUrl,
      )}
      alt={title}
    />
  );
}

ContentTileImage.propTypes = {
  bookCoverImageName: PropTypes.string,
  primary_image: RecipeImageProps.graphic,
  mobileView: PropTypes.oneOf(['grid', 'list']),
};

ContentTileImage.defaultProps = {
  bookCoverImageName: null,
  primary_image: null,
  mobileView: 'list',
};

function ContentTile(props) {
  const {
    item,
    children,
    view,
    mobileView,
    onClick,
    referrer,
    section,
    showOwnership,
    collectionId,
    hideActions,
    linkUrl,
    linkTarget,
    watermarked,
  } = props;
  const { authors, book, checked, id, title, hash, ratings, primary_image } = item;

  useStyles(CssContentTile);

  const isOwned = useSelector(state => state.user.owned_books.includes(book.isbn));
  const toRenderAuthors = formatter.authorsList.toString(authors);
  // @todo fix elastic search to always render content author
  const authorsWithFallback = toRenderAuthors || formatter.authorsList.toString(book.authors);
  const rootClassName = classNames('content-tile', {
    'content-tile--checked': checked,
    'content-tile--list': view === 'list',
    'content-tile--mobile-list': mobileView === 'list',
    'content-tile--not-owned': item.alacarte && !isOwned,
  });
  const textSize = useSelector(state => state.config.textSize);
  const isOnMobile = useMediaQuery('(max-width: 641px)');
  const [imgFigureMaxWidth, setImgFigureMaxWidth] = useState(100);
  const tileBody = useRef(null);

  const image = (
    <ContentTileImage
      bookCoverImageName={book.image_name}
      primary_image={item.primary_image}
      mobileView={mobileView}
    />
  );

  const shareImageUrl = getImageSrc(primary_image, 'square', size => size.w >= 315, {
    searchHighToLow: true,
  });

  const watermark = watermarked ? (
    <img src="images/logo-white.png" alt="ckbk" className="content-tile__watermark" />
  ) : null;

  const calculateTileTitleHeight = fontScale => {
    if (fontScale === 1) {
      return 48;
    }

    return 27.78 * fontScale + 24.72;
  };

  const calculateTileHeight = (fontScale, isGrid = true) => {
    if ((!fontScale || fontScale <= 1) && isGrid) {
      return 375;
    }

    if (fontScale === 1) {
      return 130;
    }

    let m = 84.44;
    let b = 290.56;

    if (!isGrid) {
      m = 55.56;
      b = 83.44;
    }

    return m * fontScale + b;
  };

  useEffect(
    () => {
      if (view === 'grid') {
        setImgFigureMaxWidth(undefined);

        return;
      }
      if (!tileBody.current || !textSize || (textSize >= 1 && imgFigureMaxWidth === 100)) {
        return;
      }

      if (textSize >= 1 && imgFigureMaxWidth < 100) {
        setImgFigureMaxWidth(100);
        return;
      }

      setImgFigureMaxWidth(tileBody.current.offsetHeight);
    },
    [textSize, view],
  );

  const positionValue = useMemo(
    () => {
      const position = 130 - (100 - imgFigureMaxWidth);

      if (isNaN(position)) {
        return 0;
      }

      return position;
    },
    [imgFigureMaxWidth],
  );

  const calculateContentTileHeight = () => {
    if (isOnMobile && mobileView === 'list') {
      return {
        height: calculateTileHeight(textSize, false),
        maxHeight: calculateTileHeight(textSize, false),
      };
    }

    if (!isOnMobile && view !== 'grid') {
      return {
        height: calculateTileHeight(textSize, false),
        maxHeight: calculateTileHeight(textSize, false),
      };
    }

    if (view === 'grid') {
      return {
        height: calculateTileHeight(textSize),
        maxHeight: calculateTileHeight(textSize),
      };
    }

    return undefined;
  };

  return (
    <div className={rootClassName} style={calculateContentTileHeight()}>
      {item.alacarte && view === 'grid' && showOwnership ? (
        <AlacarteStatusIndicator
          isbn={book.isbn}
          signature_={book.signature_}
          className="content-tile__alacarte"
        />
      ) : null}
      {children}
      <Link
        className="content-tile__wrapper"
        to={linkUrl || route.content(id, title)}
        state={{ imageHint: primary_image }}
        referrer={referrer}
        section={section}
        onClick={onClick}
        target={linkTarget}
      >
        <figure className="content-tile__figure" style={{ maxWidth: imgFigureMaxWidth }}>
          {image}
          {watermark}
        </figure>
        <div
          ref={tileBody}
          className={classNames('content-tile__body', {
            'content-tile__body--hide-actions': hideActions,
          })}
        >
          <header
            className="content-tile__header"
            style={{
              maxHeight: calculateTileTitleHeight(textSize),
              minHeight: calculateTileTitleHeight(textSize),
              lineHeight: `${calculateTileTitleHeight(textSize) / 2}px`,
            }}
          >
            {title}
          </header>
          <footer className="content-tile__footer">
            <p className="content-tile__paragraph">
              <strong>{book.title}</strong>
            </p>
            <p className="content-tile__paragraph content-tile__paragraph--narrow">
              {authorsWithFallback}
            </p>
          </footer>
        </div>
      </Link>

      {/* Interactive elements are overlayed using CSS to avoid nesting them inside the <a> tag. */}
      {hideActions ? null : (
        <div
          className="content-tile__actions-wrapper"
          style={
            imgFigureMaxWidth !== 100 && positionValue
              ? {
                  left: positionValue,
                  width: `calc(100%-${positionValue}px)`,
                }
              : undefined
          }
        >
          <div className="content-tile__stars">
            {ratings && ratings.count ? (
              <StarRating
                averageRating={ratings.average}
                compact
                contentHash={hash}
                contentId={id}
                count={ratings.count}
                readOnly
                size="small"
              />
            ) : null}
          </div>
          <div className="content-tile__actions">
            {view === 'list' && showOwnership ? (
              <AlacarteStatusIndicator
                isbn={book.isbn}
                signature_={book.signature_}
                diameter={24}
              />
            ) : null}
            <FavouriteButton contentId={id} contentType={CONTENT_TYPES.RECIPE} hash={hash} />
            <ContentOptionsAsIcon
              authors={authors?.length ? authors : book.authors}
              book={book}
              itemId={id}
              page={item}
              hash={hash}
              itemType={CONTENT_TYPES.CONTENT}
              contentType={CONTENT_TYPES.RECIPE}
              image={image}
              heading={Array.isArray(title) ? title[0] : title}
              collectionId={collectionId}
              imageToShare={shareImageUrl}
              urlToShare={route.content(id, title)}
              textToShare={getSharingText(title, book, authors)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

ContentTile.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  collectionId: PropTypes.number,
  hideActions: PropTypes.bool,
  item: PropTypes.shape({
    authors: PropTypes.oneOfType([AuthorsProps.authors, PropTypes.string]),
    book: PropTypes.shape({
      isbn: PropTypes.string,
      image_name: PropTypes.string,
      image_permission: PropTypes.bool,
      title: PropTypes.string,
    }),
    checked: PropTypes.bool,
    primary_image: RecipeImageProps.graphic,
    id: PropTypes.string,
    instagram_thumbnail_url: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    ratings: PropTypes.shape({ count: PropTypes.number, average: PropTypes.number }),
  }).isRequired,
  linkUrl: PropTypes.string,
  linkTarget: PropTypes.string,
  mobileView: PropTypes.oneOf(['grid', 'list']),
  onClick: PropTypes.func,
  referrer: PropTypes.string,
  section: PropTypes.string,
  showOwnership: PropTypes.bool,
  view: PropTypes.oneOf(['grid', 'list']),
  watermarked: PropTypes.bool,
};

ContentTile.defaultProps = {
  children: null,
  collectionId: null,
  hideActions: false,
  linkUrl: null,
  linkTarget: null,
  mobileView: 'list',
  onClick: () => {},
  referrer: null,
  section: null,
  showOwnership: true,
  view: 'grid',
  watermarked: false,
};

export default React.memo(ContentTile);
